import { ReactNode } from 'react'
import { Description, DialogPanel, DialogTitle } from '@headlessui/react'
import { clsx } from 'clsx'
import CloseButton from './CloseButton'
import SimpleModal from './SimpleModal'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'fit'

const sizeClasses: Record<ModalSize, string> = {
    sm: 'w-full max-w-md',
    md: 'w-full max-w-lg',
    lg: 'w-full max-w-2xl',
    xl: 'w-full max-w-4xl',
    '2xl': 'w-full max-w-6xl',
    // Sized by the content rather than the viewport, capped so wide content
    // (tables, JSON dumps) cannot outgrow the screen.
    fit: 'min-w-min max-w-4xl sm:w-fit'
}

type Props = {
    open: boolean
    onClose: () => void
    /** Sentence case, no trailing full stop — the header owns all title styling. */
    title: ReactNode
    /** Muted single-line context under the title, inside the header. */
    description?: ReactNode
    /** Optional leading icon element (typically a h-8 w-8 heroicon). */
    icon?: ReactNode
    size?: ModalSize
    /** Right-aligned action row. Use the shared Button component. */
    footer?: ReactNode
    children?: ReactNode
    hideCloseButton?: boolean
    /** Let content (dropdowns, popovers) escape the panel instead of clipping. */
    overflowVisible?: boolean
}

/**
 * The standard Arwen modal: SimpleModal shell plus a panel with a consistent
 * header (Jost 500 title, muted description, divider, close button) so call
 * sites no longer hand-roll their own DialogTitle styling. For yes/no
 * confirmation dialogs use ConfirmModal instead.
 */
export default function Modal(props: Props) {
    const {
        open,
        onClose,
        title,
        description,
        icon,
        size = 'md',
        footer,
        children,
        hideCloseButton,
        overflowVisible
    } = props

    return (
        <SimpleModal open={open} onClose={onClose}>
            <DialogPanel
                className={clsx(
                    'relative transform rounded-lg bg-white p-6 text-left shadow-xl transition-all duration-300',
                    overflowVisible ? 'overflow-visible' : 'overflow-hidden',
                    sizeClasses[size]
                )}
            >
                <div
                    className={clsx(
                        'flex gap-4 border-b border-ink-200 pb-4',
                        // With a description the icon and close button hang from the top of
                        // the header; a lone title centres against them instead.
                        description ? 'items-start' : 'items-center'
                    )}
                >
                    {icon && (
                        <div className="flex h-10 w-10 flex-none items-center justify-center">
                            {icon}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        {/* font-sans + !font-medium override the global Merriweather/400
                            h1-h4 rule in index.css — app headings are Jost 500. */}
                        <DialogTitle
                            as="h3"
                            className="font-sans !font-medium text-2xl leading-7 text-ink-900"
                        >
                            {title}
                        </DialogTitle>
                        {description && (
                            <Description as="div" className="mt-1 text-sm text-ink-500">
                                {description}
                            </Description>
                        )}
                    </div>
                    {!hideCloseButton && <CloseButton onClick={onClose} />}
                </div>

                <div className="mt-5">{children}</div>

                {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
            </DialogPanel>
        </SimpleModal>
    )
}
